import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";

import {
  fetchDocumentDownloadUrl,
  useDeleteDocument,
  useDocuments,
  useUploadDocument,
} from "@/hooks/api/useDocuments";

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Upload + list documents for any subject (tenant, landlord, ...). */
export function DocumentsPanel({
  subjectType,
  subjectId,
  title = "Documents",
  hint,
}: {
  subjectType: string;
  subjectId: string;
  title?: string;
  hint?: string;
}) {
  const { data: documents } = useDocuments(subjectType, subjectId);
  const upload = useUploadDocument(subjectType, subjectId);
  const remove = useDeleteDocument(subjectType, subjectId);
  const fileRef = useRef<HTMLInputElement>(null);

  async function download(id: string) {
    try {
      const url = await fetchDocumentDownloadUrl(id);
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Could not open the document");
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>{title}</CardTitle>
        <Button
          size="sm"
          variant="outline"
          disabled={upload.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="size-4" />
          Upload
        </Button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            upload.mutate(file, {
              onSuccess: () => toast.success("Document uploaded"),
              onError: () => toast.error("Upload failed"),
            });
            e.target.value = "";
          }}
        />
      </CardHeader>
      <CardContent>
        {hint && <p className="mb-3 text-xs text-muted-foreground">{hint}</p>}
        {documents && documents.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 py-2">
                <div className="flex items-center gap-2.5">
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {doc.source} · {formatDate(doc.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => download(doc.id)}>
                    <Download className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      remove.mutate(doc.id, {
                        onError: () => toast.error("Could not delete the document"),
                      })
                    }
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No documents yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
